import React, { useCallback, useEffect, useState } from "react";
import { Table, Button, Tag, message, Modal, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield, faUserPlus, faRotateLeft, faUserSlash } from "@fortawesome/free-solid-svg-icons";
import { format, fromUnixTime } from "date-fns";
import authApi from "../../../api/authApi";
import { PERMISSION_GROUPS, FLAG_PERMISSIONS, normalizePerms } from "../../../config/admin-permissions";
import UserPermissionModal from "../User/user-permission-modal";
import AddModModal from "./add-mod-modal";

// Map permKey -> nhãn tiếng Việt (gộp nhóm mục + quyền đặc biệt) để render tag.
const PERM_LABEL = {};
PERMISSION_GROUPS.forEach((g) => g.items.forEach((it) => (PERM_LABEL[it.key] = it.label)));
FLAG_PERMISSIONS.forEach((f) => (PERM_LABEL[f.key] = f.label));

function ModManager() {
    const [mods, setMods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [permTarget, setPermTarget] = useState(null);
    const [addOpen, setAddOpen] = useState(false);

    const fetchMods = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await authApi.getMods();
            setMods(data.mods || []);
        } catch {
            message.error("Không thể tải danh sách CTV");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMods();
    }, [fetchMods]);

    // Gỡ CTV: set role về 'user' (backend tự xoá permissions).
    const removeMod = (r) =>
        Modal.confirm({
            title: "Gỡ quyền CTV",
            content: (
                <div>
                    <p className="font-bold">#{r.userId} — {r.name}</p>
                    <p className="text-content-3 text-sm">{r.email}</p>
                    <p className="text-orange-500 text-sm mt-1">Tài khoản sẽ trở lại user thường.</p>
                </div>
            ),
            okText: "Gỡ CTV",
            okType: "danger",
            cancelText: "Hủy",
            onOk: async () => {
                await authApi.updateUserPermissions(r.userId, { role: "user", permissions: {} });
                message.success("Đã gỡ quyền CTV");
                fetchMods();
            },
        });

    const columns = [
        { title: "ID", dataIndex: "userId", key: "userId", width: 62 },
        { title: "Tên", dataIndex: "name", key: "name", ellipsis: true, width: 160 },
        { title: "Email", dataIndex: "email", key: "email", ellipsis: true, width: 200 },
        {
            title: "Quyền được cấp", key: "permissions",
            render: (_, r) => {
                const permObj = normalizePerms(r.permissions);
                const keys = Object.keys(permObj).filter((k) => permObj[k]);
                if (!keys.length) return <span className="text-content-3">Chưa cấp quyền</span>;
                return (
                    <div className="flex flex-wrap gap-1">
                        {keys.map((k) => (
                            <Tag key={k} color="blue" style={{ marginRight: 0 }}>
                                {PERM_LABEL[k] || k}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Trạng thái", dataIndex: "active", key: "active", width: 100, align: "center",
            render: (active) => <Tag color={active ? "green" : "red"}>{active ? "Hoạt động" : "Bị khóa"}</Tag>,
        },
        {
            title: "Ngày tạo", dataIndex: "createTime", key: "createTime", width: 130,
            render: (v) => v
                ? <span className="text-xs text-content-3 whitespace-nowrap">{format(fromUnixTime(v), "dd/MM/yyyy")}</span>
                : "—",
        },
        {
            title: "", key: "actions", width: 130, fixed: "right",
            render: (_, r) => (
                <div className="flex gap-1">
                    <Tooltip title="Sửa quyền">
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faUserShield} />}
                            onClick={() =>
                                setPermTarget({ id: r.userId, name: r.name, email: r.email, role: r.role, permissions: r.permissions })
                            }
                        />
                    </Tooltip>
                    <Tooltip title="Gỡ CTV">
                        <Button
                            size="small"
                            danger
                            icon={<FontAwesomeIcon icon={faUserSlash} />}
                            onClick={() => removeMod(r)}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h1 className="text-xl font-bold">Quản lý CTV / Mod</h1>
                <div className="flex gap-2">
                    <Button icon={<FontAwesomeIcon icon={faRotateLeft} />} onClick={fetchMods} title="Tải lại" />
                    <Button type="primary" icon={<FontAwesomeIcon icon={faUserPlus} />} onClick={() => setAddOpen(true)}>
                        Thêm CTV
                    </Button>
                </div>
            </div>

            <Table
                size="small"
                rowKey="userId"
                dataSource={mods}
                columns={columns}
                pagination={false}
                loading={loading}
                scroll={{ x: "max-content" }}
            />

            <span className="text-sm text-content-3">Tổng: {mods.length} CTV</span>

            <UserPermissionModal
                user={permTarget}
                onClose={() => setPermTarget(null)}
                onSuccess={() => { setPermTarget(null); fetchMods(); }}
            />

            <AddModModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSelect={(u) => { setAddOpen(false); setPermTarget(u); }}
            />
        </div>
    );
}

export default ModManager;
